import { useEffect, useRef } from 'react';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import { SerpLendingDelayTableView } from '@/components/Approval/@components/SerpLendingDelay';
import { getSerpLendingDelayDraftSourceUrl } from '@/constant/approval';
import { getNestedDataFetcher } from '@/utils/apis';
import type { SerpDocumentRef } from '@/utils/approval/serpDocument';
import {
  buildSerpLendingDelaySubject,
  type SerpLendingDelayDraftSource,
} from '@/utils/approval/serpLendingDelay';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

type SerpLendingDelayFieldProps = {
  /** 신규 작성은 진입 쿼리, 편집은 저장 attributes 의 SERP 문서 */
  serpDocument: SerpDocumentRef | null;
  isEditMode: boolean;
};

/**
 * SERP 여신유예신청(163) — 레거시 appr_edit_forms_serp_lending_delay.
 * 편집도 SERP 를 다시 조회해 표를 새로 그리고, 제목은 신규 작성 때만 자동으로 채움
 */
const SerpLendingDelayField = ({
  serpDocument,
  isEditMode,
}: SerpLendingDelayFieldProps) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'serpLendingDelay' });
  const subjectFilledRef = useRef(false);

  const typeCode = serpDocument?.typeCode;
  const sequence = serpDocument?.sequence;
  const { data, error, isLoading } = useSWR<SerpLendingDelayDraftSource>(
    typeCode && sequence
      ? getSerpLendingDelayDraftSourceUrl(typeCode, sequence)
      : null,
    getNestedDataFetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  useEffect(() => {
    const current = getValues('serpLendingDelay');
    if (
      current &&
      (current.typeCode !== typeCode || current.sequence !== sequence)
    ) {
      setValue('serpLendingDelay', null, { shouldDirty: false });
    }
  }, [typeCode, sequence, getValues, setValue]);

  useEffect(() => {
    if (!data || !typeCode || !sequence) return;
    if (
      data.attributes.typeCode !== typeCode ||
      data.attributes.sequence !== sequence
    )
      return;
    const source = data.display.source ?? {};
    const autoSubject = isEditMode
      ? ''
      : getValues('serpLendingDelay')?.autoSubject ??
        buildSerpLendingDelaySubject(source) ??
        getValues('subject') ??
        '';
    setValue(
      'serpLendingDelay',
      { typeCode, sequence, source, autoSubject },
      { shouldDirty: false },
    );
    if (isEditMode || subjectFilledRef.current) return;
    subjectFilledRef.current = true;
    setValue('subject', autoSubject, { shouldDirty: false });
  }, [data, typeCode, sequence, isEditMode, getValues, setValue]);

  const renderBody = () => {
    if (!serpDocument) {
      return (
        <ApprovalNoticeBox role="alert">
          {t('SERP 문서 정보가 없습니다.')}
        </ApprovalNoticeBox>
      );
    }
    if (state?.source) {
      return <SerpLendingDelayTableView source={state.source} t={t} />;
    }
    if (error) {
      return (
        <ApprovalNoticeBox role="alert">
          {t('데이터를 불러오지 못했습니다.')}
        </ApprovalNoticeBox>
      );
    }
    if (isLoading) {
      return (
        <ApprovalNoticeBox role="status">
          {t('데이터를 불러오는 중입니다...')}
        </ApprovalNoticeBox>
      );
    }
    return null;
  };

  return <FieldBlock className="serp-lending-delay">{renderBody()}</FieldBlock>;
};

export default SerpLendingDelayField;
